import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  Modal,
  Row,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  DragOutlined,
  ThunderboltFilled,
  ThunderboltOutlined,
  ReloadOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import {
  ProduceQueueCard,
  ProduceQueueDetail,
  QueueOrderRow,
  QueueOverview,
  fetchProduceQueue,
  fetchQueueOverview,
  reorderProduceQueue,
  setOrderUrgent,
} from "../../api/productionQueueApi";
import { useAuth } from "../../contexts/auth.context";

const { Title, Text } = Typography;

const CAN_EDIT_QUEUE = ["admin", "director", "manager"];

const nf = new Intl.NumberFormat("ru-RU");
const fmt = (n: number) => nf.format(Math.round(n));

/** Подпись срока: 0 дней — «готово сейчас», null — производительность не задана. */
function DaysTag({ days, compact = false }: { days: number | null; compact?: boolean }) {
  if (days === null) {
    return (
      <Tooltip title="Не задана дневная производительность и нет выработки за 30 дней — срок посчитать нельзя">
        <Tag color="red" icon={<WarningOutlined />}>
          срок неизвестен
        </Tag>
      </Tooltip>
    );
  }
  if (days === 0) return <Tag color="cyan">{compact ? "сейчас" : "готово сейчас"}</Tag>;
  return <Tag color={days > 7 ? "orange" : "green"}>{days} дн.</Tag>;
}

/** Полоса покрытия: сколько очереди закрыто складом, сколько нужно произвести. */
function CoverageBar({ card }: { card: ProduceQueueCard }) {
  const total = Math.max(card.queue, card.residue) || 1;
  const covered = Math.min(card.queue, card.residue);
  const pct = (v: number) => `${(v / total) * 100}%`;

  return (
    <div style={{ display: "flex", height: 10, borderRadius: 5, overflow: "hidden", background: "#f0f0f0" }}>
      {covered > 0 && (
        <Tooltip title={`Со склада: ${fmt(covered)}`}>
          <div style={{ width: pct(covered), background: "#13c2c2" }} />
        </Tooltip>
      )}
      {card.toProduce > 0 && (
        <Tooltip title={`Произвести: ${fmt(card.toProduce)}`}>
          <div style={{ width: pct(card.toProduce), background: "#faad14" }} />
        </Tooltip>
      )}
      {card.free > 0 && (
        <Tooltip title={`Свободный остаток: ${fmt(card.free)}`}>
          <div style={{ width: pct(card.free), background: "#52c41a" }} />
        </Tooltip>
      )}
    </div>
  );
}

interface SortableRowProps {
  row: QueueOrderRow;
  unit: string;
  canEdit: boolean;
  onToggleUrgent: (row: QueueOrderRow) => void;
}

function SortableQueueRow({ row, unit, canEdit, onToggleUrgent }: SortableRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: row.id,
    disabled: !canEdit,
  });

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "10px 12px",
        marginBottom: 6,
        borderRadius: 6,
        background: row.isUrgent ? "#fff1f0" : "#fafafa",
        border: row.isUrgent ? "1px solid #ffa39e" : "1px solid #f0f0f0",
      }}
    >
      {canEdit && (
        <span {...attributes} {...listeners} style={{ cursor: "grab", color: "#999" }}>
          <DragOutlined />
        </span>
      )}
      <Text strong style={{ minWidth: 24 }}>
        {row.position}
      </Text>
      <div style={{ flex: 1, minWidth: 0 }}>
        <Text ellipsis style={{ display: "block" }}>
          {row.clientName}
        </Text>
        <Text type="secondary" style={{ fontSize: 12 }}>
          нужно {fmt(row.need)} {unit}
          {row.fromStock > 0 && ` · со склада ${fmt(row.fromStock)}`}
          {row.toProduce > 0 && ` · произвести ${fmt(row.toProduce)}`}
        </Text>
      </div>
      <DaysTag days={row.days} compact />
      {canEdit && (
        <Tooltip title={row.isUrgent ? "Снять срочность" : "Пометить срочным"}>
          <Button
            size="small"
            type={row.isUrgent ? "primary" : "default"}
            danger={row.isUrgent}
            icon={row.isUrgent ? <ThunderboltFilled /> : <ThunderboltOutlined />}
            onClick={() => onToggleUrgent(row)}
          />
        </Tooltip>
      )}
    </div>
  );
}

export default function ProductionQueue() {
  const { authState } = useAuth();
  const canEdit = CAN_EDIT_QUEUE.includes(authState.user?.role ?? "");

  const [overview, setOverview] = useState<QueueOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [details, setDetails] = useState<Record<string, ProduceQueueDetail>>({});
  const [detailLoading, setDetailLoading] = useState<string | null>(null);
  const [savingFor, setSavingFor] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const loadOverview = useCallback(async () => {
    setLoading(true);
    try {
      setOverview(await fetchQueueOverview());
    } catch (error: any) {
      message.error(error?.response?.data?.error || "Не удалось загрузить очередь");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  const loadDetail = useCallback(async (produceId: string) => {
    setDetailLoading(produceId);
    try {
      const detail = await fetchProduceQueue(produceId);
      setDetails((prev) => ({ ...prev, [produceId]: detail }));
    } catch (error: any) {
      message.error(error?.response?.data?.error || "Не удалось загрузить очередь продукции");
    } finally {
      setDetailLoading(null);
    }
  }, []);

  const handleDragEnd = (produceId: string) => async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const detail = details[produceId];
    if (!detail) return;

    const oldIndex = detail.rows.findIndex((r) => r.id === active.id);
    const newIndex = detail.rows.findIndex((r) => r.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(detail.rows, oldIndex, newIndex);
    // Показываем новый порядок сразу, сроки пересчитает сервер
    setDetails((prev) => ({ ...prev, [produceId]: { ...detail, rows: reordered } }));

    setSavingFor(produceId);
    try {
      const { queue } = await reorderProduceQueue(
        produceId,
        reordered.map((r) => r.id)
      );
      setDetails((prev) => ({ ...prev, [produceId]: queue }));
      loadOverview();
      message.success("Порядок очереди сохранён");
    } catch (error: any) {
      // Откатываем на серверное состояние, чтобы экран не расходился с базой
      setDetails((prev) => ({ ...prev, [produceId]: detail }));
      message.error(error?.response?.data?.error || "Не удалось изменить порядок");
    } finally {
      setSavingFor(null);
    }
  };

  const handleToggleUrgent = (produceId: string) => async (row: QueueOrderRow) => {
    setSavingFor(produceId);
    try {
      const result = await setOrderUrgent(row.id, !row.isUrgent);
      setDetails((prev) => ({ ...prev, [produceId]: result.queue }));
      loadOverview();

      if (result.shifted.length > 0) {
        // Цена срочности: у кого-то вырос срок — это должно быть видно сразу
        Modal.info({
          title: "Срочный заказ сдвинул очередь",
          content: (
            <div>
              <p>У этих заказов вырос срок:</p>
              <ul style={{ paddingLeft: 18 }}>
                {result.shifted.map((s) => (
                  <li key={s.id}>
                    {s.clientName}: {s.wasDays ?? "?"} → <b>{s.nowDays ?? "?"}</b> дн.
                  </li>
                ))}
              </ul>
            </div>
          ),
        });
      } else {
        message.success(result.message);
      }
    } catch (error: any) {
      message.error(error?.response?.data?.error || "Не удалось изменить срочность");
    } finally {
      setSavingFor(null);
    }
  };

  const columns = useMemo(
    () => [
      {
        title: "Продукция",
        dataIndex: "produceName",
        key: "produceName",
        render: (name: string, card: ProduceQueueCard) => (
          <Space direction="vertical" size={2} style={{ width: "100%", minWidth: 200 }}>
            <Text strong>{name}</Text>
            <CoverageBar card={card} />
          </Space>
        ),
      },
      {
        title: "Заказов",
        dataIndex: "ordersCount",
        key: "ordersCount",
        width: 90,
      },
      {
        title: "В очереди",
        key: "queue",
        width: 130,
        render: (_: unknown, c: ProduceQueueCard) => `${fmt(c.queue)} ${c.unitOfMeasurement ?? ""}`,
      },
      {
        title: "На складе",
        key: "residue",
        width: 120,
        render: (_: unknown, c: ProduceQueueCard) => (
          <Text style={{ color: "#13c2c2" }}>{fmt(c.residue)}</Text>
        ),
      },
      {
        title: "Свободно",
        key: "free",
        width: 120,
        render: (_: unknown, c: ProduceQueueCard) =>
          c.free > 0 ? <Text style={{ color: "#52c41a" }}>{fmt(c.free)}</Text> : <Text type="secondary">—</Text>,
      },
      {
        title: "Произвести",
        key: "toProduce",
        width: 130,
        render: (_: unknown, c: ProduceQueueCard) =>
          c.toProduce > 0 ? <Text style={{ color: "#fa8c16" }}>{fmt(c.toProduce)}</Text> : <Text type="secondary">—</Text>,
      },
      {
        title: "В день",
        key: "capacity",
        width: 150,
        render: (_: unknown, c: ProduceQueueCard) => {
          if (c.capacity.value <= 0) return <Text type="secondary">не задана</Text>;
          const drift =
            c.capacity.source === "manual" && c.capacity.factDays > 0
              ? Math.round(((c.capacity.fact - c.capacity.value) / c.capacity.value) * 100)
              : null;
          return (
            <Space direction="vertical" size={0}>
              <Text>{fmt(c.capacity.value)}</Text>
              {drift !== null && (
                <Tooltip title={`Фактическая выработка за 30 дней — ${fmt(c.capacity.fact)} в день`}>
                  <Text type={Math.abs(drift) > 20 ? "warning" : "secondary"} style={{ fontSize: 12 }}>
                    факт {fmt(c.capacity.fact)} ({drift > 0 ? "+" : ""}
                    {drift}%)
                  </Text>
                </Tooltip>
              )}
              {c.capacity.source === "fact" && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  по факту
                </Text>
              )}
            </Space>
          );
        },
      },
      {
        title: "Закроем за",
        key: "days",
        width: 150,
        render: (_: unknown, c: ProduceQueueCard) => <DaysTag days={c.days} />,
      },
    ],
    []
  );

  if (loading && !overview) {
    return (
      <div style={{ textAlign: "center", padding: 64 }}>
        <Spin size="large" />
      </div>
    );
  }

  const produces = overview?.produces ?? [];

  return (
    <div style={{ padding: 16 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>
          Очередь производства
        </Title>
        <Button icon={<ReloadOutlined />} onClick={loadOverview} loading={loading}>
          Обновить
        </Button>
      </Row>

      {overview && produces.length > 0 && (
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={12} md={6}>
            <Card size="small">
              <Statistic title="Продукций в очереди" value={overview.totals.produces} />
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card size="small">
              <Statistic title="Активных заказов" value={overview.totals.orders} />
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card size="small">
              <Statistic
                title="Узкое место"
                value={overview.bottleneck?.days ?? "—"}
                suffix={overview.bottleneck?.days != null ? "дн." : ""}
              />
              <Text type="secondary" style={{ fontSize: 12 }}>
                {overview.bottleneck?.produceName ?? "—"}
              </Text>
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card size="small">
              <Statistic
                title="Свободный остаток"
                value={overview.totals.withFreeStock}
                suffix="поз."
                valueStyle={{ color: overview.totals.withFreeStock > 0 ? "#52c41a" : undefined }}
              />
            </Card>
          </Col>
        </Row>
      )}

      {overview && overview.totals.withoutCapacity > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={`У ${overview.totals.withoutCapacity} позиций не задана производительность`}
          description="Для них срок закрытия не рассчитывается. Заполните дневную норму в карточке продукции или дождитесь отчётов производства."
        />
      )}

      {produces.length === 0 ? (
        <Empty description="Активных заказов нет — очередь пуста" />
      ) : (
        <Table
          rowKey="produceId"
          columns={columns}
          dataSource={produces}
          pagination={false}
          scroll={{ x: 1100 }}
          expandable={{
            onExpand: (expanded, card) => {
              if (expanded && !details[card.produceId]) loadDetail(card.produceId);
            },
            expandedRowRender: (card) => {
              const detail = details[card.produceId];
              if (detailLoading === card.produceId || !detail) {
                return (
                  <div style={{ textAlign: "center", padding: 24 }}>
                    <Spin />
                  </div>
                );
              }
              const unit = detail.unitOfMeasurement ?? "";
              return (
                <Spin spinning={savingFor === card.produceId}>
                  <div style={{ padding: "4px 8px" }}>
                    {canEdit && (
                      <Text type="secondary" style={{ display: "block", marginBottom: 8, fontSize: 12 }}>
                        Перетащите заказ, чтобы изменить очередь. Молния — срочный заказ,
                        он встаёт наверх.
                      </Text>
                    )}
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleDragEnd(card.produceId)}
                    >
                      <SortableContext
                        items={detail.rows.map((r) => r.id)}
                        strategy={verticalListSortingStrategy}
                      >
                        {detail.rows.map((row) => (
                          <SortableQueueRow
                            key={row.id}
                            row={row}
                            unit={unit}
                            canEdit={canEdit}
                            onToggleUrgent={handleToggleUrgent(card.produceId)}
                          />
                        ))}
                      </SortableContext>
                    </DndContext>
                  </div>
                </Spin>
              );
            },
          }}
        />
      )}
    </div>
  );
}
